import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { Lock, RefreshCw, Unlock } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CASHIER_USERS_QUERY,
  CASH_REGISTERS_FOR_SHIFT_QUERY,
  CASH_SHIFTS_QUERY,
  CLOSE_CASH_SHIFT_MUTATION,
  MY_ACTIVE_CASH_SHIFT_QUERY,
  OPEN_CASH_SHIFT_MUTATION,
  SURPRISE_CASH_COUNT_MUTATION,
} from './cash-shifts.gql';
import { CashCutDialog } from './CashCutDialog';
import { CashCutSummaryDialog } from './CashCutSummaryDialog';
import { CashMovementsList } from './CashMovementsList';
import { CashShiftMovementDialog } from './CashShiftMovementDialog';
import { OpenShiftDialog } from './OpenShiftDialog';
import { SurpriseCountDialog } from './SurpriseCountDialog';

import { useBranchScope } from '@/modules/auth/useAuth';
import { useAuthStore } from '@/stores/authStore';
import { CASH_SHIFT_STATUS_FROM_GQL } from '@/shared/graphql/enums';
import { formatCentsString } from '@/shared/lib/money';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';

interface CashShift {
  id: string;
  branch_id: string;
  cash_register_id: string;
  cashier_id: string;
  folio_number: string | null;
  status: string;
  initial_fund_cents: string;
  opened_at: string;
  closed_at: string | null;
  notes: string | null;
}

interface CashRegister {
  id: string;
  branch_id: string;
  name: string;
  status: 'active' | 'inactive';
}

interface CashierUser {
  id: string;
  email: string;
  full_name: string;
}

interface CashCutResult {
  id: string;
  cash_shift_id: string;
  folio_number: string | null;
  initial_fund_cents: string;
  expected_cash_cents: string;
  declared_cash_cents: string;
  difference_cents: string;
  credit_card_cents: string;
  debit_card_cents: string;
  abonos_cents: string;
  cash_in_cents: string;
  cash_out_cents: string;
  cancelled_sales_cents: string;
  discounts_cents: string;
  calculation_version: string;
  snapshot_json: string | null;
  notes: string | null;
  status: string;
  created_at: string;
}

interface CloseCashShiftResponse {
  closeCashShift: {
    shift_id: string;
    cut: CashCutResult;
  };
}

interface ShiftsResponse {
  cashShifts: { items: CashShift[]; page: { total: number; has_more: boolean } };
}

interface MyActiveShiftResponse {
  myActiveCashShift: CashShift | null;
}

interface RegistersResponse {
  cashRegisters: { items: CashRegister[] };
}

interface CashiersResponse {
  cashierUsers: CashierUser[];
}

export function CashShiftsPage(): JSX.Element {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const is_admin = user?.roles.includes('administrator') ?? false;
  const branch_id = useBranchScope();

  const [show_open, setShowOpen] = useState(false);
  const [show_close_for, setShowCloseFor] = useState<CashShift | null>(null);
  const [show_movement, setShowMovement] = useState<{
    shift: CashShift;
    kind: 'cash_in' | 'cash_out';
  } | null>(null);
  const [show_surprise_for, setShowSurpriseFor] = useState<CashShift | null>(null);
  const [last_cut_result, setLastCutResult] = useState<CashCutResult | null>(null);
  const [error_text, setErrorText] = useState<string | null>(null);

  const { data: shifts_data, refetch: refetchShifts } = useQuery<ShiftsResponse>(
    CASH_SHIFTS_QUERY,
    {
      variables: { offset: 0, limit: 25, status: null, branch_id },
      fetchPolicy: 'cache-and-network',
      skip: !is_admin,
    },
  );

  const { data: my_active_data, refetch: refetchMyActive } = useQuery<MyActiveShiftResponse>(
    MY_ACTIVE_CASH_SHIFT_QUERY,
    { fetchPolicy: 'cache-and-network' },
  );

  const { data: registers_data } = useQuery<RegistersResponse>(CASH_REGISTERS_FOR_SHIFT_QUERY, {
    variables: { offset: 0, limit: 50 },
    fetchPolicy: 'cache-first',
    skip: !is_admin,
  });

  const { data: cashiers_data } = useQuery<CashiersResponse>(CASHIER_USERS_QUERY, {
    fetchPolicy: 'cache-first',
    skip: !is_admin,
  });

  const [openCashShift, { loading: opening }] = useMutation(OPEN_CASH_SHIFT_MUTATION);
  const [closeCashShift, { loading: closing }] =
    useMutation<CloseCashShiftResponse>(CLOSE_CASH_SHIFT_MUTATION);
  const [surpriseCashCount, { loading: surprising }] = useMutation(SURPRISE_CASH_COUNT_MUTATION);

  const all_shifts = (shifts_data?.cashShifts.items ?? []).map(normalizeCashShift);
  const my_active = my_active_data?.myActiveCashShift
    ? normalizeCashShift(my_active_data.myActiveCashShift)
    : null;
  const open_shifts = useMemo(() => all_shifts.filter((s) => s.status === 'open'), [all_shifts]);
  const closed_shifts = useMemo(() => all_shifts.filter((s) => s.status !== 'open'), [all_shifts]);
  const registers = registers_data?.cashRegisters.items ?? [];
  const cashiers = cashiers_data?.cashierUsers ?? [];
  const cashier_label_by_id = useMemo(
    () => new Map(cashiers.map((c) => [c.id, c.full_name])),
    [cashiers],
  );
  const register_label_by_id = useMemo(
    () => new Map(registers.map((r) => [r.id, r.name])),
    [registers],
  );

  const refresh = useCallback(async (): Promise<void> => {
    await Promise.all([refetchShifts(), refetchMyActive()]);
  }, [refetchShifts, refetchMyActive]);

  const onSubmitOpenShift = useCallback(
    async (input: {
      cash_register_id: string;
      cashier_id: string;
      initial_fund_cents: string;
      notes: string;
    }) => {
      setErrorText(null);
      try {
        await openCashShift({
          variables: {
            input: {
              cash_register_id: input.cash_register_id,
              cashier_id: input.cashier_id,
              initial_fund_cents: input.initial_fund_cents,
              notes: input.notes.trim() || null,
            },
          },
        });
        setShowOpen(false);
        await refresh();
      } catch (err) {
        applyApolloErrorMessage(err, t, setErrorText);
      }
    },
    [openCashShift, refresh, t],
  );

  const onSubmitCloseShift = useCallback(
    async (input: {
      declared_cash_cents: string;
      declared_credit_card_cents: string;
      declared_debit_card_cents: string;
      notes: string;
    }) => {
      if (!show_close_for) return;
      setErrorText(null);
      const idempotency_key =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `np-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      try {
        const result = await closeCashShift({
          variables: {
            input: {
              cash_shift_id: show_close_for.id,
              declared_cash_cents: input.declared_cash_cents,
              declared_credit_card_cents: input.declared_credit_card_cents,
              declared_debit_card_cents: input.declared_debit_card_cents,
              notes: input.notes.trim() || null,
            },
          },
          // eslint-disable-next-line @typescript-eslint/naming-convention
          context: { headers: { 'Idempotency-Key': idempotency_key } },
        });
        setShowCloseFor(null);
        setLastCutResult(result.data?.closeCashShift.cut ?? null);
        await refresh();
      } catch (err) {
        applyApolloErrorMessage(err, t, setErrorText);
      }
    },
    [closeCashShift, show_close_for, refresh, t],
  );

  const onSubmitSurprise = useCallback(
    async (input: { declared_cash_cents: string; reason: string }) => {
      if (!show_surprise_for) return;
      setErrorText(null);
      const idempotency_key =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `np-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      try {
        await surpriseCashCount({
          variables: {
            input: {
              cash_shift_id: show_surprise_for.id,
              declared_cash_cents: input.declared_cash_cents,
              reason: input.reason.trim() || null,
            },
          },
          // eslint-disable-next-line @typescript-eslint/naming-convention
          context: { headers: { 'Idempotency-Key': idempotency_key } },
        });
        setShowSurpriseFor(null);
        await refresh();
      } catch (err) {
        applyApolloErrorMessage(err, t, setErrorText);
      }
    },
    [surpriseCashCount, show_surprise_for, refresh, t],
  );

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight">{t('cash_shifts.title')}</h2>
          <p className="text-muted-foreground text-sm">{t('cash_shifts.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {is_admin ? (
            <Button type="button" onClick={() => setShowOpen(true)}>
              <Unlock className="h-4 w-4" aria-hidden />
              <span>{t('cash_shifts.open_shift')}</span>
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              void refresh();
            }}
            aria-label={t('cash_shifts.refresh')}
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </header>

      {error_text ? (
        <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-3 text-sm">
          {error_text}
        </div>
      ) : null}

      {!is_admin && my_active ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('cash_shifts.my_active_title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ShiftSummary
              shift={my_active}
              register_name={
                register_label_by_id.get(my_active.cash_register_id) ?? my_active.cash_register_id
              }
              cashier_name={user?.full_name ?? my_active.cashier_id}
            />
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowMovement({ shift: my_active, kind: 'cash_in' })}
              >
                {t('cash_shifts.add_cash_in')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowMovement({ shift: my_active, kind: 'cash_out' })}
              >
                {t('cash_shifts.add_cash_out')}
              </Button>
            </div>
            <div className="mt-4">
              <CashMovementsList shift_id={my_active.id} />
            </div>
          </CardContent>
        </Card>
      ) : null}

      {!is_admin && !my_active ? (
        <Card>
          <CardContent className="text-muted-foreground py-6 text-center text-sm">
            {t('cash_shifts.no_active_shift_cashier')}
          </CardContent>
        </Card>
      ) : null}

      {is_admin ? (
        <>
          <section className="space-y-3">
            <h3 className="text-muted-foreground text-sm font-semibold uppercase tracking-wide">
              {t('cash_shifts.open_section')} · {open_shifts.length}
            </h3>
            {open_shifts.length === 0 ? (
              <Card>
                <CardContent className="text-muted-foreground py-6 text-center text-sm">
                  {t('cash_shifts.no_open_shifts')}
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {open_shifts.map((shift) => (
                  <Card key={shift.id}>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center justify-between text-base">
                        <span>{cashier_label_by_id.get(shift.cashier_id) ?? shift.cashier_id}</span>
                        <Badge variant="success">{t('cash_shifts.status_open')}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <ShiftSummary
                        shift={shift}
                        register_name={
                          register_label_by_id.get(shift.cash_register_id) ?? shift.cash_register_id
                        }
                        cashier_name={cashier_label_by_id.get(shift.cashier_id) ?? shift.cashier_id}
                      />
                      <div className="flex flex-wrap gap-2 pt-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setShowMovement({ shift, kind: 'cash_in' })}
                        >
                          {t('cash_shifts.add_cash_in')}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setShowMovement({ shift, kind: 'cash_out' })}
                        >
                          {t('cash_shifts.add_cash_out')}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setShowSurpriseFor(shift)}
                        >
                          {t('cash_shifts.surprise_count')}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          onClick={() => setShowCloseFor(shift)}
                        >
                          <Lock className="h-4 w-4" aria-hidden />
                          <span>{t('cash_shifts.close_shift')}</span>
                        </Button>
                      </div>
                      <div className="pt-2">
                        <CashMovementsList shift_id={shift.id} />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </section>

          <section className="space-y-3">
            <h3 className="text-muted-foreground text-sm font-semibold uppercase tracking-wide">
              {t('cash_shifts.history_section')} · {closed_shifts.length}
            </h3>
            {closed_shifts.length === 0 ? (
              <Card>
                <CardContent className="text-muted-foreground py-6 text-center text-sm">
                  {t('cash_shifts.no_history')}
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {closed_shifts.map((shift) => (
                  <Card key={shift.id}>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center justify-between text-base">
                        <span>{cashier_label_by_id.get(shift.cashier_id) ?? shift.cashier_id}</span>
                        <Badge variant="muted">{t(`cash_shifts.status_${shift.status}`)}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-muted-foreground space-y-1 text-xs">
                      <div>
                        {t('cash_shifts.opened_at')}: {fmtDate(shift.opened_at)}
                      </div>
                      <div>
                        {t('cash_shifts.closed_at')}:{' '}
                        {shift.closed_at ? fmtDate(shift.closed_at) : '—'}
                      </div>
                      <div className="font-mono tabular-nums">
                        {t('cash_shifts.initial_fund')}:{' '}
                        {formatCentsString(shift.initial_fund_cents)}
                      </div>
                      {shift.folio_number ? (
                        <div>
                          {t('cash_shifts.folio')}:{' '}
                          <span className="font-mono">{shift.folio_number}</span>
                        </div>
                      ) : null}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </section>
        </>
      ) : null}

      {show_open ? (
        <OpenShiftDialog
          submitting={opening}
          registers={registers}
          cashiers={cashiers}
          on_close={() => setShowOpen(false)}
          on_submit={onSubmitOpenShift}
        />
      ) : null}

      {show_movement ? (
        <CashShiftMovementDialog
          shift={show_movement.shift}
          kind={show_movement.kind}
          on_close={() => setShowMovement(null)}
          on_submitted={() => {
            setShowMovement(null);
            void refresh();
          }}
        />
      ) : null}

      {show_close_for ? (
        <CashCutDialog
          shift={show_close_for}
          submitting={closing}
          on_close={() => setShowCloseFor(null)}
          on_submit={onSubmitCloseShift}
        />
      ) : null}

      {show_surprise_for ? (
        <SurpriseCountDialog
          shift={show_surprise_for}
          submitting={surprising}
          on_close={() => setShowSurpriseFor(null)}
          on_submit={onSubmitSurprise}
        />
      ) : null}

      {last_cut_result ? (
        <CashCutSummaryDialog cut={last_cut_result} on_close={() => setLastCutResult(null)} />
      ) : null}
    </div>
  );
}

function ShiftSummary({
  shift,
  register_name,
  cashier_name,
}: {
  shift: CashShift;
  register_name: string;
  cashier_name: string;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="space-y-1 text-sm">
      <div>
        <span className="text-muted-foreground">{t('cash_shifts.cashier')}: </span>
        <span>{cashier_name}</span>
      </div>
      <div>
        <span className="text-muted-foreground">{t('cash_shifts.register')}: </span>
        <span>{register_name}</span>
      </div>
      <div>
        <span className="text-muted-foreground">{t('cash_shifts.opened_at')}: </span>
        <span className="font-mono tabular-nums">{fmtDate(shift.opened_at)}</span>
      </div>
      <div>
        <span className="text-muted-foreground">{t('cash_shifts.initial_fund')}: </span>
        <span className="font-mono tabular-nums">{formatCentsString(shift.initial_fund_cents)}</span>
      </div>
      {shift.notes ? (
        <div>
          <span className="text-muted-foreground">{t('cash_shifts.notes')}: </span>
          <span>{shift.notes}</span>
        </div>
      ) : null}
    </div>
  );
}

function normalizeCashShift(shift: CashShift): CashShift {
  return {
    ...shift,
    status:
      CASH_SHIFT_STATUS_FROM_GQL[shift.status as keyof typeof CASH_SHIFT_STATUS_FROM_GQL] ??
      shift.status,
  };
}

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function applyApolloErrorMessage(
  err: unknown,
  t: (k: string, opts?: Record<string, unknown>) => string,
  set_error: (msg: string | null) => void,
): void {
  if (err instanceof ApolloError) {
    const code = err.graphQLErrors[0]?.message ?? err.message;
    if (code === 'cashier_already_has_open_shift') {
      set_error(t('cash_shifts.error_cashier_has_shift'));
      return;
    }
    if (code === 'register_already_has_open_shift') {
      set_error(t('cash_shifts.error_register_has_shift'));
      return;
    }
    if (code.startsWith('cash_out_exceeds_cap')) {
      const parts = code.split(':');
      const cap_cents = parts[1];
      set_error(t('cash_shifts.error_cash_out_cap', { cap: cap_cents ?? '50000' }));
      return;
    }
    if (code === 'cash_shift_required') {
      set_error(t('cash_shifts.error_shift_required'));
      return;
    }
    if (code === 'cash_shift_not_owned_by_actor') {
      set_error(t('cash_shifts.error_not_owner'));
      return;
    }
    if (code.startsWith('cash_shift_not_open')) {
      set_error(t('cash_shifts.error_not_open'));
      return;
    }
    if (code.startsWith('cash_shift_not_closable')) {
      set_error(t('cash_shifts.error_not_closable'));
      return;
    }
    if (code === 'cash_cut_already_exists') {
      set_error(t('cash_shifts.error_cut_exists'));
      return;
    }
    if (code === 'role_missing') {
      set_error(t('cash_shifts.error_admin_only'));
      return;
    }
    set_error(code);
    return;
  }
  if (err instanceof Error) set_error(err.message);
}
